import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { Spinner, Stack } from "react-bootstrap";

const HLSPlayer = ({ hlsUrl, cameraName }) => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isFullScreen, setIsFullScreen] = useState(false);

    useEffect(() => {
        const video = videoRef.current;

        if (!video || !hlsUrl) return;

        let hls;

        if (Hls.isSupported()) {
            hls = new Hls({
                lowLatencyMode: true,
                maxBufferLength: 3,
            });

            hls.loadSource(hlsUrl);
            hls.attachMedia(video);

            // ✅ MANIFEST LOADED
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                setIsLoading(false);
                video.play().catch(() => {
                });
            });

            // ✅ ERROR HANDLING (IMPORTANT PART)
            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error(`HLS ERROR`, {
                    type: data.type,
                details: data.details,
                fatal: data.fatal,
                url: hlsUrl,
                });

                // 🔥 Auto-recover logic
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            console.log(`Network error → restarting HLS`);
                            hls.startLoad();
                            break;

                        case Hls.ErrorTypes.MEDIA_ERROR:
                            console.log(`Media error → recovering`);
                            hls.recoverMediaError();
                            break;

                        default:
                           console.log(`Unrecoverable error → destroying`);
                            hls.destroy();
                            break;
                    }
                }
            });
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
            // Safari
            video.src = hlsUrl;

            video.addEventListener("loadedmetadata", () => {
                setIsLoading(false);
                video.play().catch(() => {
                });
            });
        }

        return () => {
            // Cleanup HLS instance
            if (hls) {
                hls.destroy();
            }
        };
    }, [hlsUrl]);

    useEffect(() => {
        const handleFullscreen = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener("fullscreenchange", handleFullscreen);

        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreen);
        };
    }, []);

    const toggleFullscreen = () => {
        if (!isFullScreen) {
            containerRef.current?.requestFullscreen?.();
        } else {
            document.exitFullscreen?.();
        }
    };

    return (
        <div
            ref={containerRef}
            className="position-relative w-100"
            style={{
                aspectRatio: "16/9",
                background: "#111",
                minHeight: "180px",
            }}
        >
            {isLoading && (
                <Stack
                    className="position-absolute w-100 h-100 justify-content-center align-items-center"
                    style={{
                        zIndex: 2,
                        background: "rgba(0,0,0,.4)",
                    }}
                >
                    <Spinner animation="border" className="text-white" />
                </Stack>
            )}

            <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                controls={false}
                onDoubleClick={toggleFullscreen}
                className={`w-100 h-100 cursor-pointer ${isFullScreen ? "object-fit-contain" : "object-fit-fill"}`}
            />

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    toggleFullscreen();
                }}
                className="position-absolute border-0 bg-transparent text-white end-0 bottom-0 me-2 mb-2"
                style={{ zIndex: 3 }}
            >
                {isFullScreen ? (
                    <MdFullscreenExit size={28} />
                ) : (
                    <MdFullscreen size={28} />
                )}
            </button>
        </div>
    );
};

export default HLSPlayer;